import React from 'react';
import propTypes from 'prop-types';
import shortid from 'shortid';

import ComponentSearch from 'app/ComponentSearch/container';
import { noop } from 'lib/noop';

import Filters from './Filters';
import Bar from './Bar';

import styles from './Bar.css';

/**
 * This component will render the Filter, Bar and ComponentSearch
 * components - (ComponentSearch renders the search results).
 * @param {object} props - necessary props for Search component
 * @returns {React.Component} Search component
 */
const Search = ({
  addFilter,
  checkStatusAndVisitPage,
  clearSimpleFilters,
  componentTypeSelected,
  context,
  createNewUnsavedPage,
  createSearchUrl,
  currPageNum,
  deleteFilter,
  deletePageThunk,
  duplicatePage,
  dynamicSelected,
  endDateSelected,
  filters,
  getSearchResults,
  getSearchSuggestions,
  isContainer,
  onFinish,
  onResultClick,
  openForEditing,
  pageNumStart,
  resultActions,
  results,
  resultsLoading,
  startDateSelected,
  storeResultPageNum,
  suggestions,
  suggestionsLoading,
  userIdSelected,
  visitSearch
}) => {
  const simpleFilters = filters.map(filter => ({
    k: filter.key,
    v: filter.val
  }));

  /**
   * executes on click on any of the search suggestion
   */
  const onSearch = () => {
    if (createSearchUrl) {
      visitSearch(context, simpleFilters);
    } else {
      getSearchResults(context, simpleFilters);
    }
  };

  return (
    <div
      className="d-flex flex-column border-secondary py-2 col-sm-10"
      id={styles.suggestions}
    >
      <Filters
        clearSimpleFilters={clearSimpleFilters}
        context={context}
        deleteFilter={deleteFilter}
        filters={filters}
        storeResultPageNum={storeResultPageNum}
      />
      <Bar
        addFilter={addFilter}
        componentTypeSelected={componentTypeSelected}
        context={context}
        dynamicSelected={dynamicSelected}
        endDateSelected={endDateSelected}
        filters={simpleFilters}
        getSearchResults={getSearchResults}
        getSearchSuggestions={getSearchSuggestions}
        isResultsLoading={resultsLoading}
        isSuggestionsLoading={suggestionsLoading}
        onSearch={onSearch}
        startDateSelected={startDateSelected}
        storeResultPageNum={storeResultPageNum}
        suggestions={suggestions}
        userIdSelected={userIdSelected}
      />
      {simpleFilters.length >= 1 && (
        <ComponentSearch
          checkStatusAndVisitPage={checkStatusAndVisitPage}
          context={context}
          createNewUnsavedPage={createNewUnsavedPage}
          currPageNum={currPageNum}
          deletePage={deletePageThunk}
          duplicatePage={duplicatePage}
          hideLists
          isContainer={isContainer}
          isResultsLoading={resultsLoading}
          key={shortid.generate()}
          onFinish={onFinish}
          onResultClick={onResultClick}
          openForEditing={openForEditing}
          pageNumStart={pageNumStart}
          resultActions={resultActions}
          results={results}
          storeResultPageNum={storeResultPageNum}
        />
      )}
    </div>
  );
};

Search.propTypes = {
  addFilter: propTypes.func.isRequired,
  checkStatusAndVisitPage: propTypes.func,
  clearSimpleFilters: propTypes.func,
  componentTypeSelected: propTypes.string,
  context: propTypes.string.isRequired,
  createNewUnsavedPage: propTypes.func,
  createSearchUrl: propTypes.bool,
  currPageNum: propTypes.number,
  deleteFilter: propTypes.func.isRequired,
  deletePageThunk: propTypes.func,
  duplicatePage: propTypes.func,
  dynamicSelected: propTypes.string,
  endDateSelected: propTypes.string,
  filters: propTypes.arrayOf(
    propTypes.shape({
      text: propTypes.string,
      key: propTypes.string,
      val: propTypes.oneOfType([propTypes.string, propTypes.array])
    })
  ).isRequired,
  getSearchResults: propTypes.func.isRequired,
  getSearchSuggestions: propTypes.func.isRequired,
  isContainer: propTypes.bool,
  onFinish: propTypes.func,
  onResultClick: propTypes.func,
  openForEditing: propTypes.bool,
  pageNumStart: propTypes.number,
  resultActions: propTypes.arrayOf(
    propTypes.shape({
      text: propTypes.string.isRequired,
      action: propTypes.string.isRequired
    })
  ),
  results: propTypes.arrayOf(
    propTypes.shape({
      'doc-type': propTypes.string,
      id: propTypes.string,
      name: propTypes.string,
      version: propTypes.string
    })
  ).isRequired,
  resultsLoading: propTypes.bool.isRequired,
  startDateSelected: propTypes.string,
  storeResultPageNum: propTypes.func,
  suggestions: propTypes.arrayOf(
    propTypes.shape({
      text: propTypes.string,
      key: propTypes.string,
      val: propTypes.string
    })
  ).isRequired,
  suggestionsLoading: propTypes.bool.isRequired,
  userIdSelected: propTypes.string,
  visitSearch: propTypes.func.isRequired
};

Search.defaultProps = {
  checkStatusAndVisitPage: noop,
  clearSimpleFilters: noop,
  componentTypeSelected: '',
  createNewUnsavedPage: noop,
  createSearchUrl: false,
  currPageNum: 1,
  deletePageThunk: noop,
  duplicatePage: noop,
  dynamicSelected: 'false',
  endDateSelected: '',
  isContainer: false,
  onFinish: undefined,
  onResultClick: undefined,
  openForEditing: true,
  pageNumStart: 1,
  resultActions: null,
  startDateSelected: '',
  storeResultPageNum: noop,
  userIdSelected: ''
};

export default Search;
